'use client';
import Image from '@repo/ui/src/components/Image';
import { Anchor } from '@repo/ui/src/components/Anchor';
import { linkClicked } from '@repo/ui/src/services/analytics';
import { OrderRowProps } from '@views/Account/DashboardOrderRow';
import OrderLineItem from './OrderLineItem';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import React, { Fragment } from 'react';
import { Optional } from '@repo/ui/src/types/helpers';
import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';

type Base = Optional<
  Pick<
    OrderRowProps,
    'productItems' | 'quantityText' | 'reviewLinkText' | 'giftCertificateItems' | 'giftCardLink'
  >,
  'productItems'
>;

export interface OrderRowBodyProps extends Base {
  getCurrencyString: (value: number | null | undefined, currency?: any) => string | null;
  hideReviewLink?: boolean;
  isAccountPage?: boolean;
}

const OrderRowBody = ({
  productItems,
  giftCertificateItems,
  quantityText,
  reviewLinkText,
  getCurrencyString,
  giftCardLink,
  hideReviewLink = false,
  isAccountPage = false,
}: OrderRowBodyProps) => {
  const { t } = useTranslation();
  const { locale } = useCurrentUrl();

  return (
    <ul className="pl-0 grid gap-y-5">
      {productItems?.map((lineItem, itemIndex) => (
        <Fragment key={lineItem.id}>
          <OrderLineItem
            lineItem={lineItem}
            index={itemIndex}
            quantityText={quantityText}
            reviewLinkText={reviewLinkText}
            hideReviewLink={hideReviewLink}
            isAccountPage={isAccountPage}
          />
          {lineItem.optionItems?.map((optionItem, optionIndex) => (
            <OrderLineItem
              key={`${itemIndex}-${optionIndex}`}
              lineItem={optionItem}
              index={optionIndex}
              quantityText={quantityText}
              reviewLinkText={reviewLinkText}
              isAccountPage={isAccountPage}
              hideReviewLink
            />
          ))}
        </Fragment>
      ))}
      {giftCertificateItems?.map(({ amount, toName }, i) => (
        <li
          key={`gc-${i}`}
          className="bg-white grid grid-cols-[auto_1fr] p-4 md:py-5 md:px-6 gap-4 md:gap-10"
        >
          <Image
            src="/icons/gift-card-no-background.png"
            width={100}
            height={100}
            objectFit="contain"
            alt="Gift Card Product Image"
          />
          <div className="self-center">
            <Anchor
              link={giftCardLink.link}
              className="tra-text-web-body-m-link font-bold"
              onClick={() => {
                const { origin = 'https://www.traeger.com' } = window?.location ?? {};
                linkClicked({
                  affiliation: 'website',
                  brand: 'Traeger',
                  locale,
                  link_location: isAccountPage ? 'my account_order history' : 'order_tracking',
                  link_text: t('cart.lineItemText.giftCardTitle', undefined, {
                    default: 'Gift Card',
                  }),
                  link_type: 'Internal',
                  link_url: `${origin}/${giftCardLink}`,
                });
              }}
            >
              {t('cart.lineItemText.giftCardTitle', undefined, { default: 'Gift Card' })}
            </Anchor>
            <p className="my-2 md:mt-3 tra-text-web-body-m">
              {toName}: {getCurrencyString(amount)}
            </p>
          </div>
        </li>
      ))}
    </ul>
  );
};

export default OrderRowBody;
